import { createReducer, on } from '@ngrx/store';
import ProcessActions from '../../actions/generations/process.actions';
import { GenerationActions } from '../../actions/generations/generation.actions';

export interface ProcessEntity {
  sid: string;
  parentNodeSid: string | null;
  type: 'MODULE' | 'PROCESS';
  name: string;
  description?: string;
  aiConfidence?: number;
  source?: string;
  identifier?: string;
  modifiedDate?: number;
  [key: string]: any;
}

interface ProcessState {
  // Normalized entity map
  entities: Record<string, ProcessEntity>;
  // Parent-to-children relationship
  childrenByParent: Record<string, string[]>;
  // Root-level sids for ordered iteration
  rootLevelSids: string[];
  // Total process count
  processCount: number;
}

const initialState: ProcessState = {
  entities: {},
  childrenByParent: {},
  rootLevelSids: [],
  processCount: 0
};
const ROOT_PARENT_KEY = '__ROOT__';

const processReducer = createReducer(
  initialState,
  // Replace the whole list - normalize nested structure
  on(ProcessActions.setProcess, (state, { data }) => {
    const modules = Array.isArray(data) ? data : [data];
    const entities: Record<string, ProcessEntity> = {};
    const childrenByParent: Record<string, string[]> = {};
    const rootLevelSids: string[] = [];
    let processCount = 0;

    modules.forEach((mod) => {
      const moduleSid = mod.sid ?? ROOT_PARENT_KEY;
      const moduleEntity: ProcessEntity = {
        ...mod,
        sid: moduleSid,
        parentNodeSid: null,
        type: 'MODULE',
        name: mod.name?.trim() ? mod.name : 'Placeholder Module'
      };
      delete moduleEntity.children; // Don't store children in entity

      entities[moduleSid] = moduleEntity;
      rootLevelSids.push(moduleSid);
      childrenByParent[moduleSid] = [];

      mod.children?.forEach((proc: any) => {
        if (proc.type === 'PROCESS') {
          processCount++;
          const processEntity: ProcessEntity = {
            ...proc,
            parentNodeSid: moduleSid
          };
          entities[proc.sid] = processEntity;
          childrenByParent[moduleSid].push(proc.sid);
        }
      });
    });

    return {
      ...state,
      entities,
      childrenByParent,
      rootLevelSids,
      processCount
    };
  }),

  // Add a new process to state
  on(ProcessActions.addProcessToState, (state, { data }) => {
    const newParentSid = data.parentNodeSid ?? ROOT_PARENT_KEY;

    // Validate parent exists
    if (!state.entities[newParentSid] && newParentSid !== ROOT_PARENT_KEY) {
      return state;
    }

    const processEntity: ProcessEntity = {
      ...data,
      parentNodeSid: newParentSid
    };

    const updatedChildrenByParent = {
      ...state.childrenByParent,
      [newParentSid]: [...(state.childrenByParent[newParentSid] || []), data.sid]
    };

    return {
      ...state,
      entities: { ...state.entities, [data.sid]: processEntity },
      childrenByParent: updatedChildrenByParent,
      processCount: state.processCount + 1
    };
  }),

  // Update an existing process, handles module moves
  on(ProcessActions.updateProcessInState, (state, { data }) => {
    const existingProcess = state.entities[data.sid];
    if (!existingProcess) {
      console.warn('Process not found for update:', data.sid);
      return state;
    }

    const oldParentSid = existingProcess.parentNodeSid ?? ROOT_PARENT_KEY;
    const newParentSid = data.parentNodeSid ?? ROOT_PARENT_KEY;

    // Update the entity
    const updatedEntity: ProcessEntity = {
      ...existingProcess,
      ...data,
      parentNodeSid: newParentSid
    };

    let updatedChildrenByParent = state.childrenByParent;

    // Handle parent change (move between modules)
    if (oldParentSid !== newParentSid) {
      // Remove from old parent's children
      const oldParentChildren = state.childrenByParent[oldParentSid] || [];
      const filteredOldChildren = oldParentChildren.filter((sid) => sid !== data.sid);

      // Add to new parent's children
      const newParentChildren = state.childrenByParent[newParentSid] || [];

      updatedChildrenByParent = {
        ...state.childrenByParent,
        [oldParentSid]: filteredOldChildren,
        [newParentSid]: [...newParentChildren, data.sid]
      };
    }

    return {
      ...state,
      entities: { ...state.entities, [data.sid]: updatedEntity },
      childrenByParent: updatedChildrenByParent
    };
  }),
  // Delete a process from state
  on(ProcessActions.deleteStateProcess, (state, { data }) => {
    const { parentNodeSid, sid } = data;
    const parentKey = parentNodeSid ?? ROOT_PARENT_KEY;

    // Check if process exists
    if (!state.entities[sid]) {
      return state;
    }

    // Remove from entities
    const { [sid]: removed, ...remainingEntities } = state.entities;

    // Remove from parent's children array
    const parentChildren = state.childrenByParent[parentKey] || [];
    const updatedChildren = parentChildren.filter((childSid) => childSid !== sid);

    return {
      ...state,
      entities: remainingEntities,
      childrenByParent: {
        ...state.childrenByParent,
        [parentKey]: updatedChildren
      },
      processCount: state.processCount - 1
    };
  }),
  on(ProcessActions.resetProcessState, () => initialState),
  on(GenerationActions.resetGeneration, () => initialState)
);

export { processReducer, ProcessState, ROOT_PARENT_KEY };
